import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDispatchConfig, useUpdateDispatchConfig, useTestDispatch } from '@/hooks/useUplog';
import { useCredentialsList } from '@/hooks/useCredentials';
import { useTemplatesList } from '@/hooks/useTemplates';
import type { ZenviaPlano } from '@/api/uplog.api';

const NONE = '__none__';

const ZENVIA_PLANOS: { value: ZenviaPlano; label: string; tps: number }[] = [
  { value: 'starter_specialist', label: 'Starter / Specialist', tps: 10 },
  { value: 'expert', label: 'Expert', tps: 15 },
  { value: 'professional', label: 'Professional', tps: 20 },
];

const AVAILABLE_FIELDS: { value: string; label: string }[] = [
  { value: 'documento', label: 'Documento (CNPJ/CPF)' },
  { value: 'nomeCliente', label: 'Nome do Cliente' },
  { value: 'razaoSocial', label: 'Razão Social' },
  { value: 'nomeFantasia', label: 'Nome Fantasia' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'mes', label: 'Mês' },
  { value: 'ano', label: 'Ano' },
  { value: 'boletoUrl', label: 'URL do Boleto' },
  { value: 'boletoNome', label: 'Nome do Boleto' },
  { value: 'nfseUrl', label: 'Link NFS-e (acessoNfse)' },
  { value: 'valor', label: 'Valor Total (R$ formatado)' },
  { value: 'dataVencimento', label: 'Data de Vencimento' },
];

export function DispatchConfig() {
  const { data: config, isLoading } = useDispatchConfig();
  const { mutate: update, isPending: saving } = useUpdateDispatchConfig();
  const { mutate: runTest, isPending: testing } = useTestDispatch();
  const { data: credsData } = useCredentialsList();
  const { data: templatesData } = useTemplatesList({ type: 'template', isActive: true });

  const credentials = credsData ?? [];
  const templates = templatesData?.data ?? [];

  const [credentialId, setCredentialId] = useState<string>(NONE);
  const [templateId, setTemplateId] = useState<string>(NONE);
  const [testPhone, setTestPhone] = useState('');
  const [daysDelay, setDaysDelay] = useState(1);
  const [maxDaysOverdue, setMaxDaysOverdue] = useState(30);
  const [dispatchHour, setDispatchHour] = useState('08:00');
  const [isActive, setIsActive] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [zenviaPlano, setZenviaPlano] = useState<ZenviaPlano>('starter_specialist');
  const [testDocumento, setTestDocumento] = useState('');

  useEffect(() => {
    if (config) {
      setCredentialId(config.credentialId ?? NONE);
      setTemplateId(config.templateId ?? NONE);
      setTestPhone(config.testPhone ?? '');
      setDaysDelay(config.daysDelay);
      setMaxDaysOverdue(config.maxDaysOverdue ?? 30);
      setDispatchHour(config.dispatchHour ?? '08:00');
      setIsActive(config.isActive);
      setFieldMapping(config.fieldMapping ?? {});
      setZenviaPlano(config.zenviaPlano ?? 'starter_specialist');
    }
  }, [config]);

  // Variables from the selected template (stored in template.variables as string[])
  const templateVariables = useMemo<string[]>(() => {
    if (templateId === NONE) return [];
    const t = templates.find((t) => t.id === templateId);
    if (!t || !Array.isArray(t.variables)) return [];
    return t.variables as string[];
  }, [templateId, templates]);

  function handleMappingChange(varName: string, fieldKey: string) {
    setFieldMapping((prev) => ({ ...prev, [varName]: fieldKey }));
  }

  function handleSave() {
    update({
      credentialId: credentialId === NONE ? null : credentialId,
      templateId: templateId === NONE ? null : templateId,
      testPhone: testPhone.trim() || null,
      daysDelay,
      maxDaysOverdue,
      dispatchHour,
      isActive,
      zenviaPlano,
      fieldMapping: Object.keys(fieldMapping).length > 0 ? fieldMapping : null,
    });
  }

  function handleToggle(val: boolean) {
    setIsActive(val);
    update({ isActive: val });
  }

  if (isLoading) return null;

  const canActivate = credentialId !== NONE && templateId !== NONE;
  const canTest = canActivate && testPhone.trim().length >= 8;

  const missingItems = [
    credentialId === NONE && 'credencial',
    templateId === NONE && 'template',
  ].filter(Boolean) as string[];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuração de Disparo</CardTitle>
        <CardDescription>
          Boletos normais são disparados D+N úteis após a emissão. Boletos vencidos recebem reenvio
          semanal até o limite de dias configurado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="dispatch-active">Ativar disparos automáticos</Label>
          <Switch
            id="dispatch-active"
            checked={isActive}
            onCheckedChange={handleToggle}
            disabled={saving || !canActivate}
          />
        </div>

        <div className="space-y-2">
          <Label>Credencial Zenvia (remetente)</Label>
          <Select value={credentialId} onValueChange={setCredentialId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a credencial..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Nenhuma —</SelectItem>
              {credentials.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Template de boleto</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o template..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— Nenhum —</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {templateVariables.length > 0 && (
          <div className="space-y-2">
            <Label>Variáveis do template</Label>
            <p className="text-xs text-muted-foreground">
              Mapeie cada variável do template para um campo do boleto/contato.
            </p>
            <div className="rounded-md border divide-y">
              {templateVariables.map((varName) => (
                <div key={varName} className="flex items-center gap-3 px-3 py-2">
                  <span className="w-32 shrink-0 font-mono text-sm text-muted-foreground">
                    {`{{${varName}}}`}
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <Select
                    value={fieldMapping[varName] ?? NONE}
                    onValueChange={(val) => handleMappingChange(varName, val === NONE ? '' : val)}
                  >
                    <SelectTrigger className="h-8 flex-1">
                      <SelectValue placeholder="Selecionar campo..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Não mapeado —</SelectItem>
                      {AVAILABLE_FIELDS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="test-phone">
            Telefone de teste{' '}
            <span className="text-xs text-muted-foreground">(com DDI, sem +)</span>
          </Label>
          <Input
            id="test-phone"
            placeholder="5511999999999"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="max-w-[200px]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="days-delay">Dias úteis para aguardar (D+N) após boleto disponível</Label>
          <Input
            id="days-delay"
            type="number"
            min={1}
            max={30}
            value={daysDelay}
            onChange={(e) => setDaysDelay(Number(e.target.value))}
            className="w-24"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="max-days-overdue">Máx. dias após vencimento (boletos vencidos)</Label>
          <Input
            id="max-days-overdue"
            type="number"
            min={7}
            max={365}
            value={maxDaysOverdue}
            onChange={(e) => setMaxDaysOverdue(Number(e.target.value))}
            className="w-24"
          />
          <p className="text-xs text-muted-foreground">
            Boletos vencidos serão disparados 1× por semana até este limite (
            {Math.max(1, Math.floor(maxDaysOverdue / 7))} disparos máx.).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="dispatch-hour">Horário dos disparos automáticos</Label>
          <Input
            id="dispatch-hour"
            type="time"
            value={dispatchHour}
            onChange={(e) => setDispatchHour(e.target.value)}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">
            Fuso: América/São Paulo. Os disparos ocorrem uma vez por dia neste horário.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Plano Zenvia (throttling)</Label>
          <Select value={zenviaPlano} onValueChange={(v) => setZenviaPlano(v as ZenviaPlano)}>
            <SelectTrigger className="max-w-[260px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ZENVIA_PLANOS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label} — {p.tps} msg/s
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Define o intervalo entre disparos para respeitar o limite do seu plano.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
            <Input
              placeholder="Documento (opcional)"
              value={testDocumento}
              onChange={(e) => setTestDocumento(e.target.value)}
              className="max-w-[180px]"
            />
            <Button
              variant="outline"
              onClick={() => runTest(testDocumento.trim() || undefined)}
              disabled={testing || !canTest}
            >
              {testing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disparar teste
            </Button>
          </div>
          {missingItems.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Faltando para habilitar: {missingItems.join(', ')}.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
